<script setup lang="ts">
import type { EChartsOption } from 'echarts'
import { LazyVChart } from '~/components/ui/chart'
import type {
  BankingDepositTerm,
  IBankingBlockResponseMap,
  IBankingDepositorSegment,
  IBankingDepositTerm
} from '../types'
import { useBankingNumber } from '../useBankingNumber'
import { wrapLabel } from './chart-labels'
import WidgetBankingStatsBlock from '../WidgetBankingStatsBlock.vue'

type Segment = keyof Omit<IBankingDepositorSegment, 'period'>

const SEGMENTS: Segment[] = ['individuals', 'legal', 'nonresidents']
const SEGMENT_COLORS: Record<Segment, string> = {
  individuals: '#2563eb',
  legal: '#0d9488',
  nonresidents: '#a78bfa'
}

/**
 * Terms drawn as rate bars, shortest first. 'term' is the total of the term deposits and is
 * named in the text instead. Translation keys avoid '-' and '+' that i18n paths do not accept.
 */
const TERM_BARS: { term: BankingDepositTerm; key: string }[] = [
  { term: 'settlement', key: 'settlement' },
  { term: 'demand', key: 'demand' },
  { term: '0-1m', key: 'm0_1' },
  { term: '1-3m', key: 'm1_3' },
  { term: '3-6m', key: 'm3_6' },
  { term: '6-12m', key: 'm6_12' },
  { term: '1-3y', key: 'y1_3' },
  { term: '3y+', key: 'y3_plus' }
]
/** Settlement and demand accounts are not term deposits: their bars are grey. */
const NON_TERM: BankingDepositTerm[] = ['settlement', 'demand']
const HEADLINE_TERM: BankingDepositTerm = '1-3y'

const { t } = useI18n()
const { formatSom, formatBillions, formatPercent, formatMonth } = useBankingNumber()

const { data: response } = useApi<IBankingBlockResponseMap['depositors']>(
  '/banking-statistics/blocks/depositors',
  { key: 'banking-statistics-depositors', lazy: true }
)

const segments = computed<IBankingDepositorSegment[]>(() => response.value?.data.segments ?? [])
const terms = computed<Partial<Record<BankingDepositTerm, IBankingDepositTerm>>>(() => {
  const value = response.value?.data.terms

  return value && !Array.isArray(value) ? value : {}
})
const asOf = computed(() => segments.value.at(-1)?.period ?? null)

const title = computed<{ key: string; params: Record<string, string | number> }>(() => {
  const rate = terms.value[HEADLINE_TERM]?.rate

  if (typeof rate !== 'number') {
    return { key: 'banking_statistics.depositors.title_neutral', params: {} }
  }

  return { key: 'banking_statistics.depositors.title', params: { rate: formatPercent(rate) } }
})

const structure = computed(() => {
  const demand = terms.value.demand?.volume
  const term = terms.value.term?.volume

  if (typeof demand !== 'number' || typeof term !== 'number') return null

  return t('banking_statistics.depositors.structure', { demand: formatSom(demand), term: formatSom(term) })
})

const termBars = computed(() =>
  TERM_BARS.flatMap(({ term, key }) => {
    const item = terms.value[term]

    return item && typeof item.rate === 'number'
      ? [{ term, label: t(`banking_statistics.depositors.terms.${key}`), rate: item.rate, volume: item.volume }]
      : []
  })
)

const segmentsOption = computed<EChartsOption>(() => ({
  grid: { left: 8, right: 16, top: 64, bottom: 8, containLabel: true },
  legend: { top: 0, left: 0, itemWidth: 16, textStyle: { fontSize: 12 } },
  xAxis: {
    type: 'category',
    boundaryGap: false,
    data: segments.value.map((point) => formatMonth(point.period)),
    axisTick: { show: false },
    axisLine: { lineStyle: { color: '#e5e7eb' } },
    // Labels "MM.YYYY": one per year, on January.
    axisLabel: {
      fontSize: 11,
      color: '#6b7280',
      hideOverlap: true,
      interval: (_index: number, value: string) => value.startsWith('01.'),
      formatter: (value: string) => value.slice(3)
    }
  },
  yAxis: {
    type: 'value',
    name: t('banking_statistics.unit_bn'),
    nameTextStyle: { fontSize: 11, color: '#6b7280', align: 'left' },
    axisLabel: { fontSize: 11, color: '#6b7280', formatter: (value: number) => formatBillions(value) },
    splitLine: { lineStyle: { color: '#f3f4f6' } }
  },
  tooltip: {
    trigger: 'axis',
    valueFormatter: (value) => (typeof value === 'number' ? formatSom(value) : '—')
  },
  series: SEGMENTS.map((segment) => ({
    type: 'line' as const,
    name: t(`banking_statistics.depositors.segments.${segment}`),
    stack: 'deposits',
    showSymbol: false,
    color: SEGMENT_COLORS[segment],
    lineStyle: { width: 1, color: SEGMENT_COLORS[segment] },
    areaStyle: { color: SEGMENT_COLORS[segment], opacity: 0.75 },
    emphasis: { focus: 'series' as const },
    data: segments.value.map((point) => point[segment])
  }))
}))

const termsOption = computed<EChartsOption>(() => ({
  grid: { left: 8, right: 56, top: 8, bottom: 8, containLabel: true },
  xAxis: { type: 'value', axisLabel: { show: false }, splitLine: { lineStyle: { color: '#f3f4f6' } } },
  yAxis: {
    type: 'category',
    inverse: true,
    data: termBars.value.map((bar) => bar.label),
    axisTick: { show: false },
    axisLine: { show: false },
    axisLabel: { fontSize: 11, color: '#374151', formatter: (value: string) => wrapLabel(value) }
  },
  tooltip: {
    trigger: 'item',
    formatter: (params: unknown) => {
      const bar = termBars.value[(params as { dataIndex?: number }).dataIndex ?? -1]

      if (!bar) return ''

      const volume =
        typeof bar.volume === 'number'
          ? `<br/>${t('banking_statistics.depositors.terms_volume', { volume: formatSom(bar.volume) })}`
          : ''

      return `${bar.label}: <strong>${formatPercent(bar.rate)}</strong>${volume}`
    }
  },
  series: [
    {
      type: 'bar',
      barCategoryGap: '30%',
      data: termBars.value.map((bar) => ({
        value: bar.rate,
        itemStyle: {
          color: NON_TERM.includes(bar.term) ? '#9ca3af' : bar.term === HEADLINE_TERM ? '#1d4ed8' : '#60a5fa',
          borderRadius: [0, 3, 3, 0]
        }
      })),
      label: {
        show: true,
        position: 'right',
        fontSize: 11,
        color: '#111827',
        formatter: (params: { value?: unknown }) =>
          typeof params.value === 'number' ? formatPercent(params.value) : ''
      }
    }
  ]
}))
</script>

<template>
  <WidgetBankingStatsBlock id="depositors" :title-key="title.key" :title-params="title.params" :as-of="asOf">
    <div class="grid gap-6 lg:grid-cols-2">
      <div class="min-w-0">
        <h3 class="mb-2 text-sm font-medium text-gray-700">{{ t('banking_statistics.depositors.segments_title') }}</h3>
        <LazyVChart :option="segmentsOption" autoresize class="h-72 w-full sm:h-80" />
      </div>

      <div v-if="termBars.length" class="min-w-0">
        <h3 class="mb-2 text-sm font-medium text-gray-700">{{ t('banking_statistics.depositors.terms_title') }}</h3>
        <LazyVChart :option="termsOption" autoresize class="h-72 w-full sm:h-80" />
        <p v-if="structure" class="text-muted-foreground mt-2 text-sm">{{ structure }}</p>
      </div>
    </div>
  </WidgetBankingStatsBlock>
</template>
